import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AfterViewInit, ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, output, signal } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Icons } from '../../util/Icons';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { Config } from '../../config';
import { LayoutService } from '../../../@vex/services/layout.service';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../@vex/animations/stagger.animation';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { KeycloakProfile } from 'keycloak-js';
import { Permission } from '../../services/data/permission';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { RealEstateService, ResidentService } from '@eadsysteme/ewsrealestateclient-v2';
import { AuthenticationUser, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { EnergyEfficiencySettingService, RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { SnackbarService } from '../../services/snackbar.service';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import { CustomPoliciesService, CustomPolicy } from '@eadsysteme/portalsettingclient-v1';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { PermissionsService } from '../../services/permissions.service';
import { MatDialog } from '@angular/material/dialog';

import { DatePipe } from '@angular/common';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ContactService } from '@eadsysteme/contactclient-v1';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { PolicyDialogComponent } from '../policy-dialog/policy-dialog.component';
import { RedirectDialogComponent } from './redirect-dialog/redirect-dialog.component';
import { AraSpinnerButtonComponent } from '../util/ara-spinner-button/ara-spinner-button.component';
import { ConfigService } from '../../services/config/config.service';
import { PageLayoutComponent } from '../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../@vex/components/page-layout/page-layout-header.directive';
import { PageLayoutContentDirective } from '../../../@vex/components/page-layout/page-layout-content.directive';
import { BreadcrumbsComponent } from '../../../@vex/components/breadcrumbs/breadcrumbs.component';
import { ContainerDirective } from '../../../@vex/directives/container/container.directive';
import { RecentRealEstateCacheService } from '../../../@vex/services/recent-real-estate-cache.service';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;
import EedReceptionTypeEnum = RoleSetting.ReceptionTypeEnum;

export interface PermissionTableEntity {
	permission?: Permission;
	street?: string;
	zip?: string;
	city?: string;
	propertyManagement?: string;
	activatedBy?: string;
}

@Component({
	selector: 'ara-permissions',
	templateUrl: './permissions.component.html',
	styleUrls: ['./permissions.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	imports: [
		PageLayoutComponent,
		PageLayoutHeaderDirective,
		PageLayoutContentDirective,
		BreadcrumbsComponent,
		ContainerDirective,
		MatTable,
		MatColumnDef,
		MatHeaderCell,
		MatHeaderCellDef,
		MatCellDef,
		MatCell,
		DatePipe,
		MatHeaderRow,
		MatHeaderRowDef,
		MatRowDef,
		MatRow,
		MatFormField,
		MatLabel,
		ReactiveFormsModule,
		MatError,
		TranslocoDirective,
		MatInput,
		MatIcon,
		MatTooltip,
		AraSpinnerButtonComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PermissionsComponent implements OnInit, AfterViewInit {
	translocoService = inject(TranslocoService);
	icons = inject(Icons);
	private snackBar = inject(SnackbarService);
	layoutService = inject(LayoutService);
	permissionsService = inject(PermissionsService);
	residentService = inject(ResidentService);
	energyEfficiencySettingService = inject(EnergyEfficiencySettingService);
	sessionService = inject(SessionService);
	private route = inject(ActivatedRoute);
	private dialog = inject(MatDialog);
	private realEstateService = inject(RealEstateService);
	private contactService = inject(ContactService);
	private readonly keycloak = inject(KeycloakService);
	breadCrumbService = inject(BreadCrumbService);
	customPoliciesService = inject(CustomPoliciesService);

	private readonly destroyRef = inject(DestroyRef);
	private readonly configService = inject(ConfigService);
	private readonly recentRealEstateCache = inject(RecentRealEstateCacheService);

	userProfile: KeycloakProfile | null = null;
	CODE_LENGTH = Config.CODE_LENGTH;

	permissions: PermissionTableEntity[] = [];
	dataSource: MatTableDataSource<PermissionTableEntity>;
	codeLength = new UntypedFormControl('', [Validators.required, Validators.minLength(this.CODE_LENGTH)]);

	// Single source of truth is the reactive control (codeLength). newPermissionCode proxies it so
	// the input no longer needs [(ngModel)] next to [formControl] (the deprecated dual-binding that
	// triggered the Angular forms warning). All existing call sites keep working unchanged.
	get newPermissionCode(): string {
		return this.codeLength.value ?? '';
	}

	set newPermissionCode(value: string) {
		this.codeLength.setValue(value);
	}
	roleSetting: RoleSetting;
	displayedColumns = ['code', 'activatedOn', 'permission', 'activatedBy', 'serviceCompany'];
	finishedLoading = signal(false);
	errorMsg = '';
	currentArea: number;
	activeTab = 0;
	authCodeIsBeingChecked = signal(false);
	araAppUrl: string;

	tablePolicies: Map<number, CustomPolicy[]> = new Map<number, CustomPolicy[]>();

	readonly messageEvent = output<string>();

	constructor() {
		this.araAppUrl = this.configService.getAraAppUrl();
	}

	public async ngOnInit() {
		this.currentArea = this.sessionService.effectiveArea;

		sessionStorage.setItem('activeView', window.location.href);
		this.userProfile = await this.keycloak.loadUserProfile();
		this.refreshPermissions();
		this.breadCrumbService.reset();
		this.breadCrumbService.addBreadCrumb({
			translationKey: 'permissions.permissions',
			icon: this.icons.get('holidayVillage'),
		});
	}

	ngAfterViewInit(): void {
		this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
			if (params.authcode) {
				this.newPermissionCode = params.authcode;
				this.addPermission();
			}
			if (params.code_added) {
				this.snackBar.success(this.translocoService.translate('permissions.addedCode'));
			}
		});
	}

	public refreshPermissions() {
		this.finishedLoading.set(false);
		this.permissions = [];
		const tokenParsed = this.keycloak.getKeycloakInstance().tokenParsed;
		this.permissionsService
			.getGroupPermissionsForUser(this.currentArea, tokenParsed.sub)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				switchMap((authUsers) => {
					return this.mapPermissionsWithContacts(authUsers);
				}),
				// The finalize operator will run when the stream completes or errors.
				finalize(() => this.finishedLoading.set(true))
			)
			.subscribe({
				next: (finalPermissionsList: PermissionTableEntity[]) => {
					this.permissions = finalPermissionsList;
					this.sortPermissions();
					this.dataSource = new MatTableDataSource<PermissionTableEntity>(this.permissions);
					this.loadPoliciesForPermissions();
				},
				error: (err) => {
					this.showSnackBar(err, this.translocoService.translate('errors.permissionsLoading'), 'error-snackbar');
				},
			});
	}

	private mapPermissionsWithContacts(authUsers: AuthenticationUser[]) {
		// If there are no users, return an empty array immediately.
		if (!authUsers || authUsers.length === 0) {
			return of([]);
		}

		// Flatten the structure from AuthUsers -> permissions.
		const allPermissions = this.mapUsersToPermissions(authUsers);

		if (allPermissions.length === 0) {
			return of([]);
		}

		// Create an array of Observables. Each observable will fetch the real estate/contact
		// data for one permission OR return an empty-data version immediately.
		const permissionTasks$ = this.getContactsByPermission(allPermissions);

		// Use forkJoin to run all the tasks in parallel and wait for all of them to complete.
		// It will emit a single array containing the results of all tasks.
		return forkJoin(permissionTasks$);
	}

	private mapUsersToPermissions(authUsers: AuthenticationUser[]) {
		const allPermissions = authUsers.flatMap((authUser) => {
			const userPermissions = this.permissionsService.getPermissionsFromUserAuthTO(authUser);
			// Add the authUser's name to each permission for later use.
			return userPermissions.map((permission) => ({ permission, creator: authUser.keycloakUserName }));
		});
		return allPermissions;
	}

	private getContactsByPermission(allPermissions) {
		return allPermissions.map(({ permission, creator }) => {
			if (permission.permissionType === PermissionTypeEnum.PROPERTY_MANAGEMENT) {
				return this.contactService.getContactById(permission.area, permission.permissionUserId).pipe(
					map((contact) => ({
						permission,
						city: '',
						zip: '',
						street: '',
						propertyManagement: contact.name,
						creator,
					})),
					catchError(() =>
						of({
							permission,
							city: '',
							zip: '',
							street: '',
							propertyManagement: '',
							creator,
						})
					)
				);
			} else {
				return this.realEstateService.getRealEstateByTenantUnitExportId(permission.area, permission.tenantUnitId).pipe(
					map((realEstate) => ({
						permission,
						city: realEstate.address.city,
						zip: realEstate.address.zipCode,
						street: `${realEstate.address.streets[0].streetName} ${realEstate.address.streets[0].houseNumber}`,
						creator,
					})),
					catchError(() =>
						of({
							permission,
							city: '',
							zip: '',
							street: '',
							creator,
						})
					)
				);
			}
		});
	}

	protected getServiceCompanyName(area: number): string {
		return this.sessionService.cachedServiceCompanyNames.has(area) ? this.sessionService.cachedServiceCompanyNames.get(area).replace(',', '') : '';
	}

	addPermission() {
		this.errorMsg = this.translocoService.translate('permissions.enterValidCode').replace('%', String(this.CODE_LENGTH));
		this.codeLength.markAllAsTouched();
		if (this.newPermissionCode.length < Config.CODE_LENGTH) {
			return;
		}
		this.authCodeIsBeingChecked.set(true);
		if (this.checkIfCodeAlreadyInUse(this.newPermissionCode)) {
			this.codeLength.setErrors({ incorrect: true });
			this.codeLength.markAllAsTouched();
			this.errorMsg = this.translocoService.translate('permissions.codeAlreadyInUse');
			this.authCodeIsBeingChecked.set(false);
		} else {
			this.checkPermissionType();
		}
	}

	private checkPermissionType() {
		this.permissionsService.getPermissionByAuthCode(this.newPermissionCode).subscribe({
			next: (permission: PermissionObject) => {
				if (
					permission.permissionType === PermissionTypeEnum.PROPERTY_MANAGEMENT ||
					permission.permissionType === PermissionTypeEnum.SERVICE_PROVIDER ||
					permission.permissionType === PermissionTypeEnum.OWNER
				) {
					this.checkForPolicies(permission);
				} else {
					const dialogRef = this.dialog.open(RedirectDialogComponent, {
						disableClose: true,
						autoFocus: false,
						restoreFocus: false,
					});
				}
				this.authCodeIsBeingChecked.set(false);
			},
			error: (err) => {
				this.handlePermissionsError(err);
			},
		});
	}

	private checkForPolicies(permission: PermissionObject) {
		this.customPoliciesService.getNecessaryConsents(permission?.area).subscribe((newPolicies) => {
			if (newPolicies && newPolicies.length > 0) {
				this.openPolicyDialog(newPolicies, permission?.area);
			} else {
				this.permitGroupByCode();
			}
		});
	}

	private openPolicyDialog(newPolicies: Array<CustomPolicy>, area: number) {
		const dialogRef = this.dialog.open(PolicyDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.policies = newPolicies;
		dialogRef.componentInstance.serviceCompanyArea = area;
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: { consented: boolean; dialogType: string }) => {
				if (result.consented) {
					this.permitGroupByCode();
				} else {
					this.handleMandatoryConsentError(result.dialogType);
				}
			});
	}

	private handleMandatoryConsentError(policyNaming: string) {
		this.codeLength.setErrors({ incorrect: true });
		this.codeLength.markAllAsTouched();
		this.errorMsg = this.translocoService.translate('policy.' + policyNaming + '.mandatoryAcceptance');
	}

	checkIfCodeAlreadyInUse(authCode: string) {
		// Checks if the user entered a code that´s already used by them
		for (const permissionObject of this.permissions) {
			if (permissionObject.permission.authCode === authCode) {
				return true;
			}
		}
		return false;
	}

	permitByCode(givenName, familyName, userProfileId) {
		// Check if the entered code is valid or not
		this.newPermissionCode = this.newPermissionCode.replace(/\s/g, '');
		this.permissionsService
			.permitByCode(givenName, familyName, this.newPermissionCode, userProfileId)
			.pipe(finalize(() => this.finishedLoading.set(true)))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (updatedAuthUser) => {
					if (updatedAuthUser) {
						this.recentRealEstateCache.clearAll();
						const refreshKeyCloak = this.permissions.length === 0;
						this.refreshPermissions();
						this.sessionService.userHasPermission.next(true);
						if (refreshKeyCloak) {
							// If user had no permissions, the keycloak instance has to be updated
							// for the sidebar menu to work as intended
							const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
								disableClose: true,
								autoFocus: false,
								restoreFocus: false,
							});
							dialogRef.componentInstance.confirmMessage = this.translocoService.translate('permissions.addedCode');
							dialogRef.componentInstance.showCloseButton = false;
							dialogRef
								.afterClosed()
								.pipe(takeUntilDestroyed(this.destroyRef))
								.subscribe(() => {
									this.keycloak.getKeycloakInstance().updateToken(-1);
									this.checkForResidentPermissions(updatedAuthUser);
									this.sessionService.updateSession();
								});
						} else {
							this.snackBar.success(this.translocoService.translate('permissions.addedCode'));
							this.keycloak.getKeycloakInstance().updateToken(-1);
							this.checkForResidentPermissions(updatedAuthUser);
						}
					}
				},
				error: (err) => {
					this.handlePermissionsError(err);
				},
			});
	}

	handlePermissionsError(err: any) {
		if (err.status === 409) {
			this.errorMsg = this.translocoService.translate('permissions.nameConflict');
		} else if (err.status === 412) {
			this.errorMsg = this.translocoService.translate('permissions.limitExceeded');
		} else {
			this.errorMsg = this.translocoService.translate('permissions.invalidCode');
		}
		this.codeLength.setErrors({ incorrect: true });
		this.codeLength.markAllAsTouched();
		this.authCodeIsBeingChecked.set(false);
	}

	permitGroupByCode() {
		this.newPermissionCode = this.newPermissionCode.replace(/\s/g, '');
		this.permissionsService
			.permitGroupByCode(this.newPermissionCode)
			.pipe(finalize(() => this.finishedLoading.set(true)))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (permissionGroup) => {
					if (permissionGroup) {
						this.recentRealEstateCache.clearAll();
						const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
							disableClose: true,
							autoFocus: false,
							restoreFocus: false,
						});
						dialogRef.componentInstance.confirmMessage = this.translocoService.translate('permissions.addedCode');
						dialogRef.componentInstance.showCloseButton = false;
						dialogRef.componentInstance.confirmButtonText = 'Ok';
						dialogRef
							.afterClosed()
							.pipe(takeUntilDestroyed(this.destroyRef))
							.subscribe(() => {
								this.keycloak.getKeycloakInstance().updateToken(-1);
								this.sessionService.updateSession();
								window.location.reload();
							});
					}
				},
				error: (err) => {
					this.handlePermissionsError(err);
				},
			});
	}

	getKeyCloakUserData() {
		const userProfileId = this.keycloak.getKeycloakInstance().tokenParsed.sub;
		const givenName = this.keycloak.getKeycloakInstance().tokenParsed['given_name'];
		const familyName = this.keycloak.getKeycloakInstance().tokenParsed['family_name'];
		return { userProfileId, givenName, familyName };
	}

	checkForResidentPermissions(updatedAuthUser) {
		// This method searches for resident permissions to be updated
		for (const permission of updatedAuthUser.permissionObjects) {
			if (permission.authCode === this.newPermissionCode && permission.permissionType === PermissionTypeEnum.RESIDENT) {
				this.updateResident(permission);
				break;
			}
		}
	}

	updateResident(permission: PermissionObject) {
		const area = permission.area;
		this.realEstateService
			.getRealEstateByTenantUnitExportId(area, permission.tenantUnitId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (realEstate) => {
					this.residentService.getResident(area, realEstate.id, permission.tenantUnitId, permission.permissionUserId).subscribe({
						next: () => {
							this.energyEfficiencySettingService.getRoleSetting(this.currentArea, permission.permissionUserId).subscribe({
								next: (roleSetting) => {
									this.roleSetting = roleSetting;
									this.roleSetting.receptionType = EedReceptionTypeEnum.PORTAL;
									this.energyEfficiencySettingService.createOrUpdateRoleSetting(area, realEstate.id, this.roleSetting);
								},
							});
						},
					});
				},
			});
	}

	sortPermissions() {
		this.permissions.sort((a, b) => {
			return (new Date(b.permission.codeActivatedOn) as any) - (new Date(a.permission.codeActivatedOn) as any);
		});
	}

	private showSnackBar(logMessage: string, message: string, error: string) {
		this.snackBar.error(message);
	}

	onTabChange(event: any) {
		this.activeTab = event.index;
	}

	private loadPoliciesForPermissions() {
		for (const permissionEntity of this.permissions) {
			this.customPoliciesService.getCurrentCustomPolicies(permissionEntity.permission.area).subscribe((currentPolicies) => {
				this.tablePolicies.set(permissionEntity.permission.area, currentPolicies);
			});
		}
	}

	showPolicy(policy: CustomPolicy, area: number) {
		const policies: CustomPolicy[] = [policy];
		if (policies && policies.length > 0) {
			const dialogRef = this.dialog.open(PolicyDialogComponent, {
				disableClose: true,
				autoFocus: false,
				restoreFocus: false,
			});
			dialogRef.componentInstance.policies = policies;
			dialogRef.componentInstance.consentMode = false;
			dialogRef.componentInstance.serviceCompanyArea = area;
		}
	}
}
